import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Loader2, Crown, ArrowRight, Sparkles, Scale } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useSubscription } from "@/hooks/use-subscription";
import { compareContracts } from "@/utils/analyzer.functions";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/compare")({
  head: () => ({
    meta: [
      { title: "Compare Two Contracts Side-by-Side — legaldecoder Pro" },
      {
        name: "description",
        content:
          "Paste two contracts and get a plain-English side-by-side comparison: which one is friendlier and why.",
      },
      { property: "og:title", content: "Compare Two Contracts Side-by-Side — legaldecoder Pro" },
      {
        property: "og:description",
        content: "Plain-English side-by-side comparison of two contracts.",
      },
      { property: "og:url", content: "https://legaldecoder.io/compare" },
    ],
    links: [{ rel: "canonical", href: "https://legaldecoder.io/compare" }],
  }),
  component: ComparePage,
});

type Row = { topic: string; a: string; b: string; verdict: "A" | "B" | "tie" };
type CompareResult = { summary: string; winner: "A" | "B" | "tie"; rows: Row[] };

function ComparePage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isPro, loading: subLoading } = useSubscription();
  const [textA, setTextA] = useState("");
  const [textB, setTextB] = useState("");
  const [labelA, setLabelA] = useState("Contract A");
  const [labelB, setLabelB] = useState("Contract B");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CompareResult | null>(null);

  useEffect(() => {
    if (!authLoading && !user) navigate({ to: "/auth", search: { redirect: "/compare" } as never });
  }, [authLoading, user, navigate]);

  if (authLoading || subLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!user) return null;

  const handleCompare = async () => {
    if (textA.trim().length < 40 || textB.trim().length < 40) {
      toast.error("Paste at least 40 characters for both contracts.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await compareContracts({ data: { textA, textB, labelA, labelB } });
      if (!res.ok) {
        toast.error("Comparison failed. Try again.");
        return;
      }
      setResult(res.result as CompareResult);
      setTimeout(
        () => document.getElementById("compare-results")?.scrollIntoView({ behavior: "smooth" }),
        100,
      );
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message ?? "Comparison failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="border-b border-border bg-gradient-glow py-12">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">Compare two contracts</h1>
            <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
              Paste two versions — an offer vs. a counter-offer, two leases, two NDAs — and get a
              plain-English side-by-side. We tell you which one is friendlier and why.
            </p>
            {!isPro && (
              <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-primary-glow/30 bg-primary-glow/10 px-3 py-1 text-xs font-bold text-primary-glow">
                <Crown className="h-3.5 w-3.5" /> Pro feature
              </div>
            )}
          </div>
        </section>

        <section className="py-10">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            {!isPro ? (
              <div className="rounded-2xl border border-border bg-card p-10 text-center shadow-card">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary-glow shadow-elegant">
                  <Scale className="h-7 w-7 text-primary-foreground" />
                </div>
                <h2 className="text-2xl font-bold">Side-by-side comparison is Pro-only</h2>
                <p className="mx-auto mt-3 max-w-md text-muted-foreground">
                  Use it when you're negotiating a lease, weighing two job offers, or deciding
                  between two freelance agreements.
                </p>
                <Button variant="hero" size="lg" className="mt-6 gap-2" asChild>
                  <Link to="/pricing">
                    Start 7-day Pro trial
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            ) : (
              <>
                <div className="grid gap-5 lg:grid-cols-2">
                  <ContractColumn
                    label={labelA}
                    setLabel={setLabelA}
                    text={textA}
                    setText={setTextA}
                    side="A"
                  />
                  <ContractColumn
                    label={labelB}
                    setLabel={setLabelB}
                    text={textB}
                    setText={setTextB}
                    side="B"
                  />
                </div>
                <Button
                  variant="hero"
                  size="xl"
                  className="mt-6 w-full gap-2"
                  onClick={handleCompare}
                  disabled={busy}
                >
                  {busy ? (
                    <>
                      <Sparkles className="h-5 w-5 animate-pulse" />
                      Comparing…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5" />
                      Compare side-by-side
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </Button>
              </>
            )}

            {result && <CompareResultsView result={result} labelA={labelA} labelB={labelB} />}
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
}

function ContractColumn({
  label,
  setLabel,
  text,
  setText,
  side,
}: {
  label: string;
  setLabel: (v: string) => void;
  text: string;
  setText: (v: string) => void;
  side: "A" | "B";
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="mb-2 flex items-center gap-2">
        <span
          className={cn(
            "flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold",
            side === "A"
              ? "bg-primary text-primary-foreground"
              : "bg-accent text-accent-foreground",
          )}
        >
          {side}
        </span>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm font-semibold focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20"
          maxLength={60}
        />
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`Paste ${label} here…`}
        className="min-h-[260px] w-full resize-y rounded-xl border border-input bg-background p-3 text-sm leading-relaxed focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20"
      />
      <p className="mt-1 text-xs text-muted-foreground">{text.length} chars</p>
    </div>
  );
}

function CompareResultsView({
  result,
  labelA,
  labelB,
}: {
  result: CompareResult;
  labelA: string;
  labelB: string;
}) {
  const winnerLabel =
    result.winner === "tie"
      ? "It's a tie"
      : result.winner === "A"
        ? `${labelA} is friendlier`
        : `${labelB} is friendlier`;
  return (
    <div id="compare-results" className="mt-10 scroll-mt-24">
      <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-6 shadow-card">
        <p className="text-xs font-bold uppercase tracking-wider text-primary-glow">Verdict</p>
        <h3 className="mt-1 text-2xl font-bold">{winnerLabel}</h3>
        <p className="mt-2 text-base leading-relaxed text-foreground/90">{result.summary}</p>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card shadow-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="p-4">Topic</th>
              <th className="p-4">{labelA}</th>
              <th className="p-4">{labelB}</th>
              <th className="p-4 text-right">Better</th>
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row, i) => (
              <tr key={i} className="border-t border-border align-top">
                <td className="p-4 font-semibold">{row.topic}</td>
                <td className={cn("p-4", row.verdict === "A" && "bg-success/5")}>{row.a}</td>
                <td className={cn("p-4", row.verdict === "B" && "bg-success/5")}>{row.b}</td>
                <td className="p-4 text-right">
                  <span
                    className={cn(
                      "inline-block rounded-full px-2.5 py-0.5 text-xs font-bold",
                      row.verdict === "tie"
                        ? "bg-muted text-muted-foreground"
                        : "bg-success/15 text-success",
                    )}
                  >
                    {row.verdict === "tie" ? "Tie" : row.verdict === "A" ? labelA : labelB}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-6 text-center text-xs uppercase tracking-wider text-muted-foreground">
        Not legal advice. For informational purposes only.
      </p>
    </div>
  );
}
